import classNames from 'classnames'
import type { LucideProps } from 'lucide-react'

/**
 * Icon props type alias — re-exported from Lucide for backward compatibility.
 * Components that accept `ComponentType<IconProps>` will work with any Lucide icon.
 */
export type IconProps = LucideProps

// ── Brand icons ─────────────────────────────────────────────────────────
// These are company brand logos (gotoAndPlay / NOPE) without Lucide equivalents.

interface BrandIconProps {
    className?: string
}

export const PlayIcon = (props: BrandIconProps) => (
    <svg className={classNames('icon', props.className)} viewBox="0 0 20 20">
        <path d="M12.6074 9.18831C13.5193 9.71201 13.5193 10.5682 12.6074 11.0919C11.6956 11.6156 10.2058 12.4725 9.29465 12.9955C8.38352 13.5185 7.63863 13.0904 7.63863 12.043V8.2365C7.63863 7.19051 8.3828 6.76241 9.29465 7.28541L12.6074 9.18831ZM19.9993 18.6379V10.0416C20.0088 7.77088 19.2452 5.56449 17.834 3.78543C16.4228 2.00636 14.4482 0.760581 12.2348 0.253012C10.0215 -0.254558 7.70131 0.00631434 5.65595 0.992715C3.61059 1.97912 1.96187 3.6323 0.981005 5.68032C0.000137269 7.72833 -0.254462 10.0492 0.259089 12.2612C0.77264 14.4731 2.02376 16.4444 3.80663 17.8508C5.5895 19.2571 7.79795 20.0148 10.0687 19.9991C12.3394 19.9834 14.5372 19.1954 16.3005 17.7645V18.9547C16.2999 19.0016 16.3087 19.0482 16.3263 19.0917C16.344 19.1352 16.3701 19.1747 16.4032 19.208C16.5317 19.3078 16.8185 19.5775 18.0871 19.5775C18.7106 19.6597 19.3437 19.5328 19.8872 19.2165C19.9259 19.1772 19.9558 19.1302 19.9751 19.0786C19.9944 19.027 20.0026 18.9719 19.9993 18.9168V18.6379ZM9.99246 16.8441C8.64833 16.8375 7.33628 16.4329 6.22191 15.6813C5.10753 14.9297 4.24077 13.8648 3.73101 12.6211C3.22125 11.3774 3.09134 10.0105 3.35768 8.693C3.62402 7.3755 4.27466 6.16643 5.2275 5.21837C6.18033 4.2703 7.39266 3.62574 8.71147 3.36602C10.0303 3.10631 11.3965 3.24308 12.6376 3.75908C13.8788 4.27507 14.9393 5.14717 15.6853 6.26531C16.4313 7.38344 16.8293 8.6975 16.8292 10.0416C16.8228 11.8497 16.0993 13.5814 14.8176 14.8567C13.5359 16.132 11.8006 16.8468 9.99246 16.8441Z" />
    </svg>
)

export const NopeIcon = (props: BrandIconProps) => (
    <svg className={classNames('icon', props.className)} viewBox="0 0 20 20">
        <path d="M15.7154 20L9.99846 14.2785L4.27692 19.9954L0 15.7154L5.72154 9.99846L0.00461578 4.27692L4.28462 0L10.0015 5.72154L15.7231 0.00461542L20 4.28462L14.2785 10.0015L19.9954 15.7231L15.7154 20Z" />
    </svg>
)

// ── Custom icons ────────────────────────────────────────────────────────
// SVG icons sourced outside the Solar set (Iconify).

interface SvgIconProps {
    size?: number
    className?: string
}

/** Plain X (close). Solar only ships circle/square close glyphs, so this bare
 *  cross is used for the unified modal close button. */
export const CloseIcon = ({ size = 24, className }: SvgIconProps) => (
    <svg
        className={className}
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <path d="M18 6 6 18M6 6l12 12" />
    </svg>
)

/** Six-dot vertical drag handle — Iconify `ci:drag-vertical`. */
export const DragVerticalIcon = ({ size = 24, className }: SvgIconProps) => (
    <svg
        className={className}
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        aria-hidden="true"
    >
        <path d="M14 18a1 1 0 1 0 2 0a1 1 0 0 0-2 0m-6 0a1 1 0 1 0 2 0a1 1 0 0 0-2 0m6-6a1 1 0 1 0 2 0a1 1 0 0 0-2 0m-6 0a1 1 0 1 0 2 0a1 1 0 0 0-2 0m6-6a1 1 0 1 0 2 0a1 1 0 0 0-2 0M8 6a1 1 0 1 0 2 0a1 1 0 0 0-2 0" />
    </svg>
)

// ── Sidebar-toggle glyphs ───────────────────────────────────────────────
// A rounded panel with a left rail. The default (rest) state is the bare
// panel; the hover variants add a chevron pointing the way the sidebar will
// move (left = collapse when open, right = expand when closed). Stroked with
// currentColor so they inherit the toggle's hover colour.

/** Panel only — the resting sidebar-toggle glyph. */
export const SidebarPanelIcon = ({ size = 16, className }: SvgIconProps) => (
    <svg
        className={className}
        width={size}
        height={size}
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        aria-hidden="true"
    >
        <path d="M14.6667 8.66667C14.6667 11.1808 14.6667 12.4379 13.8856 13.219C13.1046 14 11.8475 14 9.33333 14L6.66667 14C4.15253 14 2.8954 14 2.1144 13.219C1.33333 12.4379 1.33333 11.1808 1.33333 8.66667L1.33333 7.33333C1.33333 4.8192 1.33333 3.56207 2.1144 2.78107C2.8954 2 4.15253 2 6.66666 2L9.33333 2C11.8475 2 13.1046 2 13.8856 2.78107C14.6667 3.56207 14.6667 4.8192 14.6667 7.33333L14.6667 8.66667Z" />
        <path d="M5 4L5 12" strokeLinecap="round" />
    </svg>
)

/** Panel + left chevron — shown on hover when the sidebar is open (collapse). */
export const SidebarCollapseIcon = ({ size = 16, className }: SvgIconProps) => (
    <svg
        className={className}
        width={size}
        height={size}
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        aria-hidden="true"
    >
        <path d="M14.6667 8.66667C14.6667 11.1808 14.6667 12.4379 13.8856 13.219C13.1046 14 11.8475 14 9.33333 14L6.66667 14C4.15253 14 2.8954 14 2.1144 13.219C1.33333 12.4379 1.33333 11.1808 1.33333 8.66667L1.33333 7.33333C1.33333 4.8192 1.33333 3.56207 2.1144 2.78107C2.8954 2 4.15253 2 6.66666 2L9.33333 2C11.8475 2 13.1046 2 13.8856 2.78107C14.6667 3.56207 14.6667 4.8192 14.6667 7.33333L14.6667 8.66667Z" />
        <path d="M5 4L5 12" strokeLinecap="round" />
        <path d="M10.7426 10.8269L8.36396 8.75204C7.90708 8.35352 7.90708 7.64337 8.36396 7.24484L10.7426 5.17001" />
    </svg>
)

/** Panel + right chevron — shown on hover when the sidebar is closed (expand). */
export const SidebarExpandIcon = ({ size = 16, className }: SvgIconProps) => (
    <svg
        className={className}
        width={size}
        height={size}
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        aria-hidden="true"
    >
        <path d="M14.6667 8.66667C14.6667 11.1808 14.6667 12.4379 13.8856 13.219C13.1046 14 11.8475 14 9.33334 14L6.66667 14C4.15254 14 2.8954 14 2.1144 13.219C1.33334 12.4379 1.33334 11.1808 1.33334 8.66667L1.33334 7.33333C1.33333 4.8192 1.33333 3.56207 2.1144 2.78107C2.8954 2 4.15253 2 6.66667 2L9.33333 2C11.8475 2 13.1046 2 13.8856 2.78107C14.6667 3.56207 14.6667 4.8192 14.6667 7.33333L14.6667 8.66667Z" />
        <path d="M5 4L5 12" strokeLinecap="round" />
        <path d="M8.5 10.8269L10.8787 8.75204C11.3356 8.35352 11.3356 7.64337 10.8787 7.24484L8.5 5.17001" />
    </svg>
)
